import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { traditionalVsVerysight } from "@/config/site";

export default function WhyChooseUs() {
  const { traditional, verysight } = traditionalVsVerysight;
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative py-28 md:py-36 overflow-hidden"
    >
      <div className="vs-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-[#d4af37]" />
            <span className="text-overline">Why VerySight</span>
          </div>
          <h2 className="mt-5 font-serif-display text-4xl md:text-6xl text-white tracking-tight leading-[1.02]">
            Two different jobs.
            <br />
            <span className="italic vs-gold-text">One critical distinction.</span>
          </h2>
          <p className="mt-6 text-white/70 font-body max-w-xl leading-relaxed">
            Your finance team keeps score. We help you win the game. Both matter — but
            they are not interchangeable.
          </p>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* Traditional column — dimmed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="relative rounded-sm border border-white/10 p-8 md:p-10 bg-white/[0.015]"
            data-testid="compare-traditional"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">
              What most businesses have
            </div>
            <h3 className="mt-3 font-serif-display text-3xl text-white/70">
              {traditional.title}
            </h3>
            <p className="mt-2 text-sm text-white/45 font-body">{traditional.subtitle}</p>

            <ul className="mt-8 space-y-3">
              {traditional.items.map((i) => (
                <li key={i} className="flex items-center gap-3 text-white/55">
                  <span className="h-6 w-6 rounded-full border border-white/15 flex items-center justify-center text-white/40">
                    <X size={12} />
                  </span>
                  <span className="font-body text-[15px]">{i}</span>
                </li>
              ))}
            </ul>
            <div
              aria-hidden
              className="absolute inset-0 rounded-sm pointer-events-none"
              style={{ background: "radial-gradient(circle at 50% 100%, rgba(0,0,0,0.4), transparent 60%)" }}
            />
          </motion.div>

          {/* VerySight column — illuminated */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative rounded-sm border border-[#d4af37]/40 p-8 md:p-10 vs-glass-strong overflow-hidden"
            data-testid="compare-verysight"
          >
            <div
              aria-hidden
              className="absolute -top-20 -right-16 h-64 w-64 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(212,175,55,0.18), transparent 70%)",
                filter: "blur(20px)",
              }}
            />
            <div className="relative">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]">
                What growing businesses need
              </div>
              <h3 className="mt-3 font-serif-display text-3xl text-white">
                {verysight.title}
              </h3>
              <p className="mt-2 text-sm text-white/70 font-body">{verysight.subtitle}</p>

              <ul className="mt-8 space-y-3">
                {verysight.items.map((i) => (
                  <li key={i} className="flex items-center gap-3 text-white">
                    <span className="h-6 w-6 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37]">
                      <Check size={12} strokeWidth={2.5} />
                    </span>
                    <span className="font-body text-[15px]">{i}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
